import React, { useRef, useState, useEffect, useMemo } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import classNames from "classnames";
import moment from "moment";
import qs from "qs";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import DealCard from "../deal-card/index.jsx";
import { getQuery } from "@/service/http";
import { PaySelector } from "../deal-payment/index.jsx";
import { List, ListItem, ConfirmModal } from "../common/index.jsx";
import Restaurant from "@/components/dc-restaurant/index.jsx";
import MapView from "@/components/dc-map-view/index.jsx";
import { Terms as VoucherTerms } from "@/components/dc-voucher";
import VoucherCard from "@/pages/vouchers/components/Item.jsx";
import VoucherDetail from "@/pages/vouchers/components/Detail.jsx";
import buyDealAPI, { client } from "@/service/buy-deal.js";
import orderAPI from "@/service/order.js";
import DealPrivacy from "../deal-privacy.jsx";
import "./index.less";
import paySuccessImg from "../images/pay_success.svg";
import orderQtyImg from "../images/order_qty.svg";
import orderVoucherImg from "../images/order_voucher.svg";
import orderPayImg from "../images/order_pay.svg";
import orderDateImg from "../images/order_date.svg";
import orderInfoImg from "../images/order_info.svg";
import orderNoImg from "../images/order_no.svg";
import payClosedImg from "../images/pay_closed.svg";
import payRefundImg from "../images/pay_refund.svg";
import payRefundingImg from "../images/pay_refunding.svg";
import payRedeemImg from "../images/pay_redeem.svg";
import qrTipImg from "../images/qr_tip.svg";
import qrDemoImg from "../images/qr_demo.png";
import checkRedImg from "../images/check_red.svg";
import mapImg from "../images/map.svg";
import telImg from "../images/tel.svg";
import copyImg from "../images/copy.svg";

export default function DealOrder({
  isPc,
  orderId,
  hasAnimate = true,
  showBack,
  onBack,
  onDealClick,
  baseUrl,
}) {
  const { t } = useTranslation();
  const [orderInfo, setOrderInfo] = useState({
    order: {},
    deal: null,
    restaurant: {},
  });
  const [animate, setAnimate] = useState("in");

  function goBack() {
    if (!hasAnimate) {
      return onBack();
    }
    setAnimate("out");
    setTimeout(() => {
      onBack();
    }, 250);
  }

  function getOrderInfo() {
    orderAPI.getOrderById(orderId).then((res) => {
      setOrderInfo((pre) => ({ ...pre, order: res }));
    });
  }

  function getOrderInfoAll() {
    if (!orderId) return;
    orderAPI
      .getOrderInfo(orderId)
      .then(async (res) => {
        if (!res) return;
        res.onDealClick = onDealClick;
        setOrderInfo(res);
      })
      .catch((err) => {
        console.error(err);
        message.error(err?.data?.full_messages?.[0] || err.message);
      });
  }

  // 进入订单详情，需要重新获取deal和餐厅信息，和订单信息，不依赖外部，参数只需要uuid
  useEffect(() => {
    getOrderInfoAll();
  }, []);

  if (!orderId) return null;

  return (
    <div
      className={`buydeal-order buydeal-popup`}
      data-animate={hasAnimate ? animate : ""}
    >
      <div className="buydeal-popup-header">
        <div className="header-left" onClick={goBack}>
          {showBack && <i className="icon icon-arrow-left"></i>}
        </div>
        <strong>{t("walkin.My Order")}</strong>
        <span className="header-right"></span>
      </div>
      <div className="buydeal-popup-line" hidden={!isPc}></div>
      {orderInfo.order?.page_status === "pending" && (
        <Pending {...orderInfo} baseUrl={baseUrl} getOrderInfo={getOrderInfo} />
      )}
      {orderInfo.order?.page_status === "successful" && (
        <Success {...orderInfo} getOrderInfo={getOrderInfo} />
      )}
      {orderInfo.order?.page_status === "refunding" && (
        <Refunding {...orderInfo} getOrderInfo={getOrderInfo} />
      )}
      {orderInfo.order?.page_status === "refunded" && (
        <Refunded {...orderInfo} getOrderInfo={getOrderInfo} />
      )}
      {orderInfo.order?.page_status === "redeemed" && (
        <Redeemed {...orderInfo} getOrderInfo={getOrderInfo} />
      )}
      {orderInfo.order?.page_status === "closed" && (
        <Closed {...orderInfo} getOrderInfo={getOrderInfo} />
      )}
      {orderInfo.order?.page_status === "expired" && (
        <Expired {...orderInfo} getOrderInfo={getOrderInfo} />
      )}
    </div>
  );
}

function Pending(props) {
  const { deal, restaurant, order, getOrderInfo, project, baseUrl } = props;
  const { t } = useTranslation();
  const payRef = useRef();
  const timeRef = useRef();
  const [showModal, setShowModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({});
  const [paymentList, setPaymentList] = useState([]);
  const [payMethod, setPayMethod] = useState({});
  const [weappConfig, setWeappConfig] = useState({});
  const showWxLunch = useMemo(() => {
    return payMethod.keyword === "uac_upacp" && client === "weixin";
  }, [payMethod]);
  const payCallback = (status) => {
    // 支付成功，重新获取订单信息
    if (status === "successed") {
      clearInterval(timeRef.current);
      getOrderInfo();
    }
  };
  const hasVoucher = order.vouchers?.length > 0;
  const voucherDesc = hasVoucher
    ? `${order.vouchers[0].detail.title} * ${order.vouchers.length}`
    : "";
  const getSuccessUrl = () => {
    let query = qs.stringify(
      {
        payment: true,
        private_token: window.private_token,
        order_id: order.uuid,
      },
      { delimiter: "-" },
    );
    return baseUrl + query;
  };
  /**
   * 获取支付方式列表
   * @returns
   */
  function getPaymentList() {
    return buyDealAPI
      .getPaymentMethods({
        restaurant_id: restaurant.id,
        project: project,
      })
      .then((res) => {
        setPaymentList(res);
        setPayMethod(
          res.find((item) => item.keyword === order.payment?.channel) || {},
        );
      });
  }

  useEffect(() => {
    getPaymentList();
    buyDealAPI
      .getPaymentInfo({
        restaurantId: restaurant.id,
        vouchers: (order.vouchers || []).map((item) => item.code),
        deal_id: deal?.id,
        project: project,
        number: order.quantity,
        payment_id: order.payment?.id,
      })
      .then((res) => {
        setPaymentInfo(res);
      });
    buyDealAPI.getWxConfig();
  }, []);

  useEffect(() => {
    if (!showWxLunch) {
      setWeappConfig({});
    } else {
      // 切换到云闪付小程序支付，需要获取授权码信息
      buyDealAPI
        .prePayment({
          channel: payMethod.keyword,
          uuid: order.uuid,
          success_url: getSuccessUrl(),
        })
        .then((data) => {
          setWeappConfig({
            appid: data.miniPayRequest.cqpMpAppId,
            path: data.miniPayRequest.cqpMpPath,
          });
        });
    }
  }, [showWxLunch]);
  
  // 轮询扫码订单状态
  useEffect(() => {
    timeRef.current = setInterval(() => {
      getOrderInfo();
    }, 3000);
    return () => {
      clearInterval(timeRef.current);
    };
  }, []);

  // 唤起支付
  const payHandler = () => {
    payRef.current.callPay();
  };
  // 取消订单
  const onCancel = () => {
    setShowModal(false);
    orderAPI
      .cancelOrder(order.uuid)
      .then(() => {
        getOrderInfo();
      })
      .catch((err) => {
        console.error(err);
        message.error(err?.data?.full_messages?.[0] || err.message);
      });
  };
  return (
    <React.Fragment>
      {showModal && (
        <ConfirmModal onCancel={() => setShowModal(false)} onOk={onCancel}>
          {t("walkin.cancel tip")}
        </ConfirmModal>
      )}
      <div className="buydeal-popup-body">
        <div className="buydeal-order--pending">
          <div
            className="buydeal-alert"
            style={{ background: "#E89028", color: "#fff" }}
          >
            <img src={paySuccessImg} alt="" />
            <span>{order.page_status_humanize}</span>
          </div>
          <DealCard deal={deal}></DealCard>
          <ListItem
            icon={orderQtyImg}
            title={t("walkin.Quantity")}
            value={order.quantity}
          ></ListItem>
          {/* 支付方式 */}
          <PaySelector
            baseUrl={baseUrl}
            payRef={payRef}
            payMethod={payMethod}
            setPayMethod={setPayMethod}
            orderInfo={order}
            paymentInfo={paymentInfo}
            paymentMethods={paymentList}
            payCallback={payCallback}
          ></PaySelector>
          <ListItem
            hidden={!hasVoucher}
            icon={orderVoucherImg}
            title={t("walkin.Voucher")}
            value={voucherDesc}
          ></ListItem>
          <div className="buydeal-popup-line" style={{ margin: 0 }}></div>
          {/* 价格 */}
          <ListItem
            title={t("walkin.Subtotal")}
            value={
              <span style={{ fontWeight: 700 }}>
                {order.format_total_amount}
              </span>
            }
          ></ListItem>

          {/* 条款 */}
          <DealPrivacy></DealPrivacy>

          <ListItem
            icon={orderNoImg}
            title={t("walkin.Order No")}
            value={order.code}
          ></ListItem>
        </div>
      </div>
      <div className="buydeal-popup-footer">
        <div className="buydeal-cashier-amount">
          <div className="amount-now">{paymentInfo.format_amount}</div>
          {hasVoucher && (
            <div className="amount-old">
              <span>{t("walkin.Original")}: </span>
              <span style={{ textDecoration: "line-through" }}>
                {paymentInfo.format_origin_amount}
              </span>
            </div>
          )}
        </div>
        <div className="buydeal-popup-btn" onClick={() => setShowModal(true)}>
          {t("CANCEL")}
        </div>
        <div
          className={classNames("buydeal-popup-btn primary", {
            disabled: !payMethod.name,
          })}
          onClick={showWxLunch ? null : payHandler}
        >
          {t("PAYMENT.Pay now")}
          <WxOpenLaunch {...weappConfig}>{t("PAYMENT.Pay now")}</WxOpenLaunch>
        </div>
      </div>
    </React.Fragment>
  );
}

function Success(props) {
  const { deal, order, restaurant, getOrderInfo } = props;
  const { t } = useTranslation();
  const ref = useRef({});
  const [showModal, setShowModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  // 退款
  function onRefund() {
    if (ref.current.loading) return;
    ref.current.loading = true;
    orderAPI
      .cancelOrder(order.uuid)
      .then(() => {
        getOrderInfo();
        ref.current.loading = false;
      })
      .catch((err) => {
        console.error(err);
        message.error(err?.data?.full_messages?.[0] || err.message);
        ref.current.loading = false;
      });
  }

  return (
    <div className="buydeal-popup-body buydeal-order--refunded">
      {showModal && (
        <ConfirmModal onCancel={() => setShowModal(false)} onOk={onRefund}>
          {t("walkin.refund tip")}
        </ConfirmModal>
      )}
      {cancelModal && (
        <ConfirmModal onCancel={() => setCancelModal(false)} onOk={onRefund}>
          {t("walkin.cancel tip")}
        </ConfirmModal>
      )}
      <div
        className="buydeal-alert"
        style={{ background: "#AFD468", color: "#fff" }}
      >
        <img src={paySuccessImg} alt="" />
        <span>{order.page_status_humanize}</span>
      </div>

      <DealCard deal={deal} onDealClick={props.onDealClick}></DealCard>
      <div className="buydeal-qrcode">
        <div className="buydeal-qrcode-img-wrap">
          <img
            className="buydeal-qrcode-img"
            src={order.qr_code?.qrcode_url}
            width={160}
            height={160}
          />
        </div>
        <div className="buydeal-qrcode-code">
          {t("walkin.Qr Code")} {order.qr_code?.key}
        </div>
      </div>
      <div className="buydeal-qrcode-notice">{t("walkin.To redeem")}</div>
      <div className="buydeal-qrcode-notice-desc" style={{ marginBottom: 32 }}>
        <img src={qrTipImg} alt="" />
        <span>{t("walkin.The order can be found in Me")}</span>
      </div>
      <List
        list={[
          {
            title: t("walkin.Quantity"),
            value: order.quantity,
            icon: orderQtyImg,
          },
          {
            title: t("walkin.Available date"),
            value: `${moment(deal?.date_start).format("yyyy.MM.DD")} — ${moment(
              deal?.date_end,
            ).format("yyyy.MM.DD")}`,
            icon: orderDateImg,
            hidden: !deal,
          },
          {
            title: t("PAYMENT.Payment method"),
            value: order.payment?.channel_humanize,
            icon: orderPayImg,
            hidden: !deal?.enable_pre_pay,
          },
          {
            title: t("walkin.Voucher"),
            value: `${order.vouchers[0]?.detail?.title} * ${order.vouchers?.length}`,
            icon: orderVoucherImg,
            hidden: !order.vouchers?.length,
          },
          {
            title: t("WEB.Amount paid"),
            value: order.format_total_amount,
            icon: orderInfoImg,
            hidden: !deal?.enable_pre_pay,
          },
          {
            title: t("walkin.Order No"),
            value: order.code,
            icon: orderNoImg,
          },
        ]}
      ></List>

      {/* 退款按钮 */}
      {!!order.operations.can_refund && (
        <ListItem
          style={{ marginTop: 16 }}
          value={
            <span
              className="buydeal-popup-btn"
              onClick={() => setShowModal(true)}
            >
              {t("walkin.Request Refund")}
            </span>
          }
        ></ListItem>
      )}

      {/* 取消按钮 */}
      {!!order.operations.can_cancel && (
        <ListItem
          style={{ marginTop: 16 }}
          value={
            <span
              className="buydeal-popup-btn"
              onClick={() => setCancelModal(true)}
            >
              {t("CANCEL")}
            </span>
          }
        ></ListItem>
      )}

      {/* 优惠券列表 */}
      <Voucher order={order}></Voucher>

      {/*  餐厅卡片 */}
      <ListItem title={t("walkin.Restaurant info")}></ListItem>
      <Restaurant restaurant={restaurant} only={true}></Restaurant>
      {/*  餐厅地图 */}
      <div style={{ position: "relative", zIndex: showModal ? 0 : 1 }}>
        <MapView {...restaurant} mapId="deal-map" />
      </div>
      <List
        list={[
          {
            icon: mapImg,
            title: (
              <span style={{ fontWeight: 400 }}>{restaurant.address}</span>
            ),
            value: (
              <CopyToClipboard
                text={restaurant.address}
                onCopy={() => message.success(t("Copied code"))}
              >
                <img src={copyImg} />
              </CopyToClipboard>
            ),
          },
          {
            icon: telImg,
            title: (
              <a
                style={{ fontWeight: 400, color: "#333" }}
                href={`tel:${restaurant.phone}`}
              >
                {restaurant.phone}
              </a>
            ),
            value: (
              <CopyToClipboard
                text={restaurant.phone}
                onCopy={() => message.success(t("Copied code"))}
              >
                <img src={copyImg} />
              </CopyToClipboard>
            ),
          },
        ]}
      ></List>
    </div>
  );
}

function Closed(props) {
  const { deal, restaurant, order, getOrderInfo, project } = props;
  const { t } = useTranslation();
  return (
    <div className="buydeal-popup-body buydeal-order--closed">
      <div
        className="buydeal-alert"
        style={{ background: "#ECECEC", color: "#999" }}
      >
        <img src={payClosedImg} alt="" />
        <span>{order.page_status_humanize}</span>
      </div>
      <DealCard deal={deal} onDealClick={props.onDealClick}></DealCard>
      <List
        list={[
          {
            title: t("walkin.Quantity"),
            value: order.quantity,
            icon: orderQtyImg,
          },
          {
            title: t("walkin.Order No"),
            value: order.code,
            icon: orderNoImg,
          },
        ]}
      ></List>
    </div>
  );
}

function Expired(props) {
  const { deal, restaurant, order, getOrderInfo, project } = props;
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const ref = useRef({});
  // 退款
  function onRefund() {
    if (ref.current.loading) return;
    ref.current.loading = true;
    orderAPI
      .cancelOrder(order.uuid)
      .then(() => {
        getOrderInfo();
        ref.current.loading = false;
      })
      .catch((err) => {
        console.error(err);
        message.error(err?.data?.full_messages?.[0] || err.message);
        ref.current.loading = false;
      });
  }
  return (
    <div className="buydeal-popup-body buydeal-order--closed">
      <div
        className="buydeal-alert"
        style={{ background: "#FFCF48", color: "#fff" }}
      >
        <img src={payClosedImg} alt="" />
        <span>{order.page_status_humanize}</span>
      </div>
      <DealCard deal={deal} onDealClick={props.onDealClick}></DealCard>
      <List
        list={[
          {
            title: t("walkin.Quantity"),
            value: order.quantity,
            icon: orderQtyImg,
          },
          {
            title: t("PAYMENT.Payment method"),
            value: order.payment?.channel_humanize,
            icon: orderPayImg,
            hidden: !deal.enable_pre_pay,
          },
          {
            title: t("walkin.Voucher"),
            value: `${order.vouchers[0]?.detail?.title} * ${order.vouchers?.length}`,
            icon: orderVoucherImg,
            hidden: !order.vouchers?.length,
          },
          {
            title: t("WEB.Amount paid"),
            value: order.format_total_amount,
            icon: orderInfoImg,
            hidden: !deal.enable_pre_pay,
          },
          {
            title: t("walkin.Order No"),
            value: order.code,
            icon: orderNoImg,
          },
        ]}
      ></List>
      {/* 退款按钮 */}
      {!!order.operations.can_refund && (
        <ListItem
          style={{ marginTop: 16 }}
          value={
            <span
              className="buydeal-popup-btn"
              onClick={() => setShowModal(true)}
            >
              {t("walkin.Request Refund")}
            </span>
          }
        ></ListItem>
      )}
      <Voucher order={order}></Voucher>
      {showModal && (
        <ConfirmModal onCancel={() => setShowModal(false)} onOk={onRefund}>
          {t("walkin.refund tip")}
        </ConfirmModal>
      )}
    </div>
  );
}

function Refunded(props) {
  const { deal, restaurant, order, getOrderInfo, project } = props;
  const { t } = useTranslation();
  return (
    <div className="buydeal-popup-body buydeal-order--refunded">
      <div
        className="buydeal-alert"
        style={{ background: "#ECECEC", color: "#999" }}
      >
        <img src={payRefundImg} alt="" />
        <span>{order.page_status_humanize}</span>
      </div>
      <div className="buydeal-order-tip">{t("walkin.refunded tip")}</div>
      <DealCard deal={deal} onDealClick={props.onDealClick}></DealCard>
      <List
        list={[
          {
            title: t("walkin.Quantity"),
            value: order.quantity,
            icon: orderQtyImg,
          },
          {
            title: t("PAYMENT.Payment method"),
            value: order.payment?.channel_humanize,
            icon: orderPayImg,
          },
          {
            title: t("walkin.Voucher"),
            value: `${order.vouchers[0]?.detail?.title} * ${order.vouchers?.length}`,
            icon: orderVoucherImg,
            hidden: !order.vouchers?.length,
          },
          {
            title: t("WEB.Amount paid"),
            value: order.format_total_amount,
            icon: orderInfoImg,
          },
          {
            title: t("WEB.Refund amount"),
            value: order.payment?.format_refund_price,
            icon: orderInfoImg,
          },
          {
            title: t("walkin.Order No"),
            value: order.code,
            icon: orderNoImg,
          },
        ]}
      ></List>
      {/* 优惠券列表 */}
      <Voucher order={order}></Voucher>
    </div>
  );
}

function Refunding(props) {
  const { deal, order, getOrderInfo } = props;
  const ref = useRef({});
  const { t } = useTranslation();
  // 轮询扫码订单状态
  useEffect(() => {
    ref.current.timer = setInterval(() => {
      getOrderInfo();
    }, 3000);
    return () => {
      clearInterval(ref.current.timer);
    };
  }, []);
  return (
    <div className="buydeal-popup-body buydeal-order--refunding">
      <div
        className="buydeal-alert"
        style={{ background: "#FFCF48", color: "#fff" }}
      >
        <img src={payRefundingImg} alt="" />
        <span>{order.page_status_humanize}</span>
      </div>
      <div className="buydeal-order-tip">{t("walkin.refunding tip")}</div>
      <DealCard deal={deal} onDealClick={props.onDealClick}></DealCard>
      <List
        list={[
          {
            title: t("walkin.Quantity"),
            value: order.quantity,
            icon: orderQtyImg,
          },
          {
            title: t("PAYMENT.Payment method"),
            value: order.payment?.channel_humanize,
            icon: orderPayImg,
          },
          {
            title: t("walkin.Voucher"),
            value: `${order.vouchers[0]?.detail?.title} * ${order.vouchers?.length}`,
            icon: orderVoucherImg,
            hidden: !order.vouchers?.length,
          },
          {
            title: t("WEB.Amount paid"),
            value: order.format_total_amount,
            icon: orderInfoImg,
          },
          {
            title: t("WEB.Refund amount"),
            value: order.payment?.format_refund_price,
            icon: orderInfoImg,
          },
          {
            title: t("walkin.Order No"),
            value: order.code,
            icon: orderNoImg,
          },
        ]}
      ></List>
      {/* 优惠券列表 */}
      <Voucher order={order}></Voucher>
    </div>
  );
}

function Redeemed(props) {
  const { deal, order } = props;
  const { t } = useTranslation();
  return (
    <div className="buydeal-popup-body buydeal-order--refunding">
      <div
        className="buydeal-alert"
        style={{ background: "#ECECEC", color: "#999" }}
      >
        <img src={payRedeemImg} alt="" />
        <span>{order.page_status_humanize}</span>
      </div>
      <DealCard deal={deal} onDealClick={props.onDealClick}></DealCard>
      <div className="buydeal-qrcode">
        <div className="buydeal-qrcode-img-wrap">
          <img
            className="buydeal-qrcode-img"
            src={qrDemoImg}
            width={160}
            height={160}
          />
          <div className="buydeal-qrcode-img-mask"></div>
          <div className="buydeal-qrcode-status">
            <img src={checkRedImg} width={32} height={32} />
            <div>{order.page_status_humanize}</div>
          </div>
        </div>

        <div className="buydeal-qrcode-code">
          {t("walkin.Qr Code")} {order.qr_code?.key}
        </div>
      </div>
      <div className="buydeal-qrcode-notice">
        {t("walkin.Redeemed Successfully")}
      </div>
      <div className="buydeal-qrcode-notice-desc" style={{ marginBottom: 32 }}>
        <img src={qrTipImg} alt="" />
        <span>{t("walkin.The order can be found in Me")}</span>
      </div>
      <List
        list={[
          {
            title: t("walkin.Quantity"),
            value: order.quantity,
            icon: orderQtyImg,
          },
          {
            title: t("walkin.Available date"),
            value: `${moment(deal?.date_start).format("yyyy.MM.DD")} — ${moment(
              deal?.date_end,
            ).format("yyyy.MM.DD")}`,
            icon: orderDateImg,
            hidden: !deal,
          },
          {
            title: t("PAYMENT.Payment method"),
            value: order.payment?.channel_humanize,
            icon: orderPayImg,
            hidden: !deal?.enable_pre_pay,
          },
          {
            title: t("walkin.Voucher"),
            value: `${order.vouchers[0]?.detail?.title} * ${order.vouchers?.length}`,
            icon: orderVoucherImg,
            hidden: !order.vouchers?.length,
          },
          {
            title: t("WEB.Amount paid"),
            value: order.format_total_amount,
            icon: orderInfoImg,
            hidden: !deal?.enable_pre_pay,
          },
          {
            title: t("walkin.Order No"),
            value: order.code,
            icon: orderNoImg,
          },
        ]}
      ></List>
      {/* 优惠券列表 */}
      <Voucher order={order}></Voucher>
    </div>
  );
}

function Voucher(props) {
  const { order } = props;
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState("in");
  const ref = useRef({});
  function onBack() {
    setAnimate("out");
    setTimeout(() => {
      setShow(false);
      setAnimate("in");
    }, 250);
  }
  function showTerm(item) {
    ref.current.term = item.detail.desc;
    setVisible(true);
  }
  function onSelect(item) {
    // 未登录用户，不能查看详情
    const { private_token } = getQuery();
    if (!private_token) return;
    ref.current.voucher_id = item.id;
    setShow(true);
  }

  if (!order.vouchers?.length) return null;

  return (
    <div className="buydeal-order-voucher">
      <div className="order-voucher-title">{t("walkin.Voucher")}</div>
      <div className="order-voucher-body">
        {order.vouchers.map((item, index) => {
          return (
            <VoucherCard
              key={index}
              voucher={item}
              onSelect={() => onSelect(item)}
              showTerm={() => showTerm(item)}
            ></VoucherCard>
          );
        })}
      </div>
      <VoucherTerms
        visible={visible}
        setVisible={setVisible}
        text={ref.current.term}
      ></VoucherTerms>
      {show && (
        <div className="buydeal-popup animate" data-animate={animate}>
          <div className="buydeal-popup-header">
            <div className="header-left" onClick={onBack}>
              <i className="icon icon-arrow-left"></i>
            </div>
            <strong>{t("VOUCHERS.Voucher details")}</strong>
            <span className="header-right"></span>
          </div>
          <div className="buydeal-popup-body">
            {show && (
              <VoucherDetail id={ref.current.voucher_id}></VoucherDetail>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 唤起微信小程序
 * @param {*} props
 * @returns
 */
const WxOpenLaunch = (props) => {
  return (
    <wx-open-launch-weapp
      id="launch-btn"
      appid={props.appid}
      path={props.path}
      style={{
        position: "absolute",
        width: props.appid ? "100%" : "1px",
        height: "38px",
        opacity: 0,
        top: 0,
        left: 0,
        zIndex: 10,
      }}
    >
      <script type="text/wxtag-template">
        <button
          style={{
            position: "absolute",
            width: props.appid ? "100%" : "1px",
            height: "38px",
            opacity: 0,
            top: 0,
            left: 0,
            zIndex: 10,
          }}
        >
          {props.children}
        </button>
      </script>
    </wx-open-launch-weapp>
  );
};
